// Four small charts above the Activity list: turn volume, median
// grounding, total cost, median retrieval latency. Single fetch
// against /me/activity/stats?window=… — the window selector is the
// only interaction here. The purpose is anomaly-spotting, not analysis.

import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { ActivityUiState } from "@/hooks/useActivityFilters";
import type { ActivityStatsResponse, ActivityWindow } from "@/services/api/me";
import { cn } from "@/utils/cn";

const WINDOWS: ReadonlyArray<{ value: ActivityWindow; label: string }> = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
];

function zeroFillDaily(
  daily: ActivityStatsResponse["daily"],
  windowDays: number,
): ActivityStatsResponse["daily"] {
  // Backend omits days with no traffic. Front-fill with zero rows so
  // the line/bar charts show a continuous x-axis instead of skipping
  // gaps. Cheap (window ≤ 90).
  const byDate = new Map(daily.map((d) => [d.bucket, d]));
  const out: ActivityStatsResponse["daily"] = [];
  const now = new Date();
  const start = new Date(now);
  start.setUTCDate(start.getUTCDate() - (windowDays - 1));
  for (let i = 0; i < windowDays; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    const bucket = d.toISOString().slice(0, 10);
    out.push(
      byDate.get(bucket) ?? {
        bucket,
        turn_count: 0,
        median_grounding: null,
        cost_usd: "0",
        median_latency_ms: null,
        error_count: 0,
      },
    );
  }
  return out;
}

interface ChartPanelProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

function ChartPanel({ title, subtitle, children }: ChartPanelProps) {
  return (
    <div className="rounded border border-ruleline bg-parchment-50 p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="smallcaps text-oxblood-500">{title}</span>
        {subtitle && (
          <span className="font-mono text-[0.7rem] text-warmgray-500">{subtitle}</span>
        )}
      </div>
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          {children as React.ReactElement}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const AXIS_TICK = { fontSize: 10, fill: "#9b8e85" };
const GRID_STROKE = "#e7dccf";

function tickDate(value: string): string {
  // YYYY-MM-DD → MM/DD
  return value.length >= 10 ? value.slice(5).replace("-", "/") : value;
}

export function ActivityChartStrip({
  state,
  stats,
  isLoading,
}: {
  state: ActivityUiState;
  stats: ActivityStatsResponse | undefined;
  isLoading: boolean;
}) {
  const { window, setWindow } = state;

  const daily = useMemo(() => {
    if (!stats) return [];
    return zeroFillDaily(stats.daily, stats.window_days).map((d) => ({
      ...d,
      cost_usd_num: Number(d.cost_usd) || 0,
    }));
  }, [stats]);

  return (
    <section className="mb-6">
      <header className="mb-3 flex items-baseline justify-between">
        <div>
          <p className="smallcaps text-oxblood-500">Trends</p>
          <h2 className="font-display text-xl text-espresso-700">
            Last {window}
            {stats && (
              <span className="ml-3 font-mono text-xs font-normal text-warmgray-500">
                {stats.totals.turn_count} turns · ${stats.totals.cost_usd}
                {stats.totals.error_count > 0 && (
                  <span className="ml-2 text-oxblood-500">
                    {stats.totals.error_count} error
                    {stats.totals.error_count === 1 ? "" : "s"}
                  </span>
                )}
              </span>
            )}
          </h2>
        </div>
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              type="button"
              onClick={() => setWindow(w.value)}
              className={cn(
                "rounded-sm border px-2 py-0.5 font-mono text-[0.7rem] uppercase tracking-wider transition-colors",
                w.value === window
                  ? "border-oxblood-500 bg-oxblood-500 text-parchment-50"
                  : "border-ruleline bg-parchment-50 text-warmgray-500 hover:border-oxblood-500",
              )}
            >
              {w.label}
            </button>
          ))}
        </div>
      </header>

      {isLoading && !stats ? (
        <div className="rounded border border-ruleline bg-parchment-50 p-6 text-center text-sm italic text-warmgray-400">
          Loading trends…
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <ChartPanel title="Volume" subtitle="turns / day">
            <LineChart data={daily} margin={{ top: 4, right: 6, bottom: 4, left: -16 }}>
              <CartesianGrid stroke={GRID_STROKE} strokeDasharray="2 2" vertical={false} />
              <XAxis dataKey="bucket" tickFormatter={tickDate} tick={AXIS_TICK} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} width={28} />
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                labelFormatter={(v) => `Date ${v}`}
                formatter={(value: number) => [value, "turns"]}
              />
              <Line
                type="monotone"
                dataKey="turn_count"
                stroke="#7a1f1f"
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ChartPanel>

          <ChartPanel title="Grounding" subtitle="median / day">
            <LineChart data={daily} margin={{ top: 4, right: 6, bottom: 4, left: -16 }}>
              <CartesianGrid stroke={GRID_STROKE} strokeDasharray="2 2" vertical={false} />
              <XAxis dataKey="bucket" tickFormatter={tickDate} tick={AXIS_TICK} />
              <YAxis domain={[0, 1]} tick={AXIS_TICK} width={28} />
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                labelFormatter={(v) => `Date ${v}`}
                formatter={(value) => {
                  const n = typeof value === "number" ? value : null;
                  return [n == null ? "—" : n.toFixed(2), "grounding"];
                }}
              />
              <Line
                type="monotone"
                dataKey="median_grounding"
                stroke="#3a6b4a"
                strokeWidth={1.5}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ChartPanel>

          <ChartPanel title="Cost" subtitle="$ / day">
            <BarChart data={daily} margin={{ top: 4, right: 6, bottom: 4, left: -16 }}>
              <CartesianGrid stroke={GRID_STROKE} strokeDasharray="2 2" vertical={false} />
              <XAxis dataKey="bucket" tickFormatter={tickDate} tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} width={28} />
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                labelFormatter={(v) => `Date ${v}`}
                formatter={(value: number) => [`$${value.toFixed(2)}`, "cost"]}
              />
              <Bar dataKey="cost_usd_num" fill="#7a1f1f" isAnimationActive={false} />
            </BarChart>
          </ChartPanel>

          <ChartPanel title="Retrieval" subtitle="median ms / day">
            <LineChart data={daily} margin={{ top: 4, right: 6, bottom: 4, left: -8 }}>
              <CartesianGrid stroke={GRID_STROKE} strokeDasharray="2 2" vertical={false} />
              <XAxis dataKey="bucket" tickFormatter={tickDate} tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} width={40} />
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                labelFormatter={(v) => `Date ${v}`}
                formatter={(value) => {
                  const n = typeof value === "number" ? value : null;
                  return [n == null ? "—" : `${n} ms`, "latency"];
                }}
              />
              <Line
                type="monotone"
                dataKey="median_latency_ms"
                stroke="#7a1f1f"
                strokeWidth={1.5}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ChartPanel>
        </div>
      )}
    </section>
  );
}
